/**
 * Pure helpers for applying user-added synthetic line breaks to a
 * translated caption phrase.
 *
 * Synthetic breaks live on the caption track (not on translation
 * data) and store only `textPosition`. The split's time position is
 * derived from `textPosition` via the active `SplitStrategy` from
 * `karaokeWordTiming.ts` — the same strategy that drives per-word
 * karaoke highlighting — so the highlight cursor crosses a synthetic
 * break exactly when it crosses the underlying text position.
 *
 * No MobX, no store imports. Strictly transformations on
 * `{ text, ts, endTs }` phrase units and `CaptionLineBreak` records.
 */

import { sameOriginalIndices } from '@src/editor/models/karaoke/CaptionLineBreak';
import { KaraokeClip } from '@src/editor/models/karaoke/KaraokeClip';
import { joinPhraseValues } from '@src/utility/strings';

import { redealPhraseSoftBreaks } from './captionSoftBreaks';
import {
  computeProportionAtTextPosition,
  DEFAULT_SPLIT_STRATEGY,
  type SplitStrategy,
} from './karaokeWordTiming';

import type { CaptionLineBreak } from '@src/editor/models/karaoke/CaptionLineBreak';

export type PhraseUnit = { text: string; ts: number; endTs: number };

/**
 * Resolve a proportional split (0..1) on a phrase's `[ts, endTs]`
 * window into an absolute timestamp.
 */
export function resolveSyntheticSplit(
  phraseTs: number,
  phraseEndTs: number,
  splitProportion: number,
): number {
  return phraseTs + (phraseEndTs - phraseTs) * splitProportion;
}

/**
 * Split phrase text at `position`, trimming the boundary so each
 * half is a clean caption line.
 */
export function splitTranslatedTextAt(
  text: string,
  position: number,
): [string, string] {
  return [text.slice(0, position).trimEnd(), text.slice(position).trimStart()];
}

/**
 * Apply the synthetic-break records that target this phrase, returning
 * the resulting sub-units in `textPosition` order. Each sub-unit
 * carries its own `[ts, endTs]` derived via the active strategy.
 *
 * - Records with invalid `textPosition` (`<= 0` or `>= text.length`)
 *   are filtered silently.
 * - Records whose derived `splitProportion` ends up `<= 0` or `>= 1`
 *   (e.g. the snap-to-word-boundary collapses to phrase start/end)
 *   are filtered silently.
 * - With no valid breaks, returns `[phrase]` unchanged.
 */
export function applySyntheticBreaksToPhrase(
  phrase: PhraseUnit,
  // Only the text position is read — lets the produce-time over-length
  // splitter (PLAT-11481) fabricate positional breaks without inventing
  // full CaptionLineBreak records.
  breaks: ReadonlyArray<Pick<CaptionLineBreak, 'textPosition'>>,
  strategy: SplitStrategy = DEFAULT_SPLIT_STRATEGY,
): PhraseUnit[] {
  if (breaks.length === 0) return [phrase];

  const resolved = breaks
    .filter(b => b.textPosition > 0 && b.textPosition < phrase.text.length)
    .map(b => ({
      textPosition: b.textPosition,
      splitProportion: computeProportionAtTextPosition(
        phrase.text,
        b.textPosition,
        strategy,
      ),
    }))
    .filter(b => b.splitProportion > 0 && b.splitProportion < 1)
    .sort((a, b) => a.textPosition - b.textPosition);

  if (resolved.length === 0) return [phrase];

  const units: PhraseUnit[] = [];
  let lastTextPos = 0;
  let lastTs = phrase.ts;
  for (const br of resolved) {
    const splitTs = resolveSyntheticSplit(
      phrase.ts,
      phrase.endTs,
      br.splitProportion,
    );
    units.push({
      text: phrase.text.slice(lastTextPos, br.textPosition).trim(),
      ts: lastTs,
      endTs: splitTs,
    });
    lastTextPos = br.textPosition;
    lastTs = splitTs;
  }
  units.push({
    text: phrase.text.slice(lastTextPos).trim(),
    ts: lastTs,
    endTs: phrase.endTs,
  });

  return units.filter(u => u.text.length > 0);
}

/**
 * Shared helper for `AdjustSyntheticCaptionBreakCommand` and
 * `RemoveSyntheticCaptionBreakCommand`. Both commands need to:
 *
 *   1. Locate every clip in `prevClips` whose `phraseOriginalIndices`
 *      matches the target phrase identity.
 *   2. Reconstitute the parent phrase's `[ts, endTs]` window and
 *      joined translated text from the first/last sub-clips.
 *   3. Re-apply the resolved set of breaks for that phrase.
 *   4. Build replacement `KaraokeClip` instances with correctly-
 *      anchored `startSecondsOffset` / `endSecondsOffset` and the
 *      preserved `siblingGroupId`.
 *
 * Returns `null` when the phrase has no surviving clips, when phrase
 * timing can't be reconstituted (missing ts/endTs on the first/last
 * sub-clip), or when applying the breaks produces zero sub-units.
 *
 * Pure transformation — no editor / track / store access.
 */
export interface RebuildPhraseClipsArgs {
  prevClips: readonly KaraokeClip[];
  phraseOriginalIndices: number[];
  /** Breaks already filtered to the target phrase. */
  breaksForPhrase: readonly CaptionLineBreak[];
  strategy?: SplitStrategy;
}

export interface RebuildPhraseClipsResult {
  phraseClipIndices: number[];
  replacementClips: KaraokeClip[];
}

export function rebuildPhraseClips(
  args: RebuildPhraseClipsArgs,
): RebuildPhraseClipsResult | null {
  const { prevClips, phraseOriginalIndices, breaksForPhrase, strategy } = args;

  const phraseClipIndices: number[] = [];
  for (let i = 0; i < prevClips.length; i++) {
    const c = prevClips[i];
    if (
      c.phraseOriginalIndices &&
      sameOriginalIndices(c.phraseOriginalIndices, phraseOriginalIndices)
    ) {
      phraseClipIndices.push(i);
    }
  }
  if (phraseClipIndices.length === 0) return null;

  const firstClip = prevClips[phraseClipIndices[0]];
  const lastClip = prevClips[phraseClipIndices[phraseClipIndices.length - 1]];
  // Re-join the pieces the way the caption rows were split: a space only
  // where one belongs (PLAT-11835 — an unconditional space glued Japanese
  // halves back as `日本 語` and shifted the remaining breaks' positions).
  const joinedText = joinPhraseValues(
    phraseClipIndices
      .map(i => (prevClips[i].translatedPhrases?.[0]?.text ?? '').trim())
      .filter(text => text.length > 0),
  ).replace(/\s+/g, ' ');
  const phraseTs = firstClip.translatedPhrases?.[0]?.ts;
  const phraseEndTs = lastClip.translatedPhrases?.[0]?.endTs;
  if (phraseTs == null || phraseEndTs == null) return null;

  const phraseUnit: PhraseUnit = {
    text: joinedText,
    ts: phraseTs,
    endTs: phraseEndTs,
  };
  const subUnits = applySyntheticBreaksToPhrase(
    phraseUnit,
    breaksForPhrase,
    strategy,
  );
  if (subUnits.length === 0) return null;

  // Preserve the sibling group across the rebuild. The phrase's
  // existing sub-clips share a sibling id (set by the original Add);
  // inherit it on every replacement clip so hover-grouping survives.
  const inheritedSiblingGroupId = firstClip.siblingGroupId;
  // PLAT-11914: each row's soft breaks follow their characters to the
  // replacement row that now holds them.
  const softBreaks = redealPhraseSoftBreaks(
    joinedText,
    phraseClipIndices.map(i => ({
      text: prevClips[i].translatedPhrases?.[0]?.text ?? '',
      softBreaks: prevClips[i].softBreakPhrasePositions,
    })),
    subUnits.map(unit => unit.text),
  );

  const replacementClips = subUnits.map((unit, i) => {
    const isFirst = i === 0;
    const isLast = i === subUnits.length - 1;
    return new KaraokeClip({
      id:
        prevClips[phraseClipIndices[i]]?.id ??
        (isFirst ? firstClip.id : `${firstClip.id}-sub-${i}`),
      transcriptTokens: firstClip.transcriptTokens,
      hiddenTranscriptTokenIds: [...firstClip.hiddenTranscriptTokenIds],
      ignoredByFiltersIds: [...firstClip.ignoredByFiltersIds],
      karaokeConfig: firstClip.karaokeConfig,
      track: firstClip.track,
      translatedText: unit.text,
      translatedPhrases: [unit],
      phraseOriginalIndices,
      siblingGroupId: inheritedSiblingGroupId,
      softBreakPhrasePositions: softBreaks[i],
      startSecondsOffset: isFirst
        ? firstClip.startSecondsOffset
        : unit.ts - phraseUnit.ts,
      endSecondsOffset: isLast
        ? lastClip.endSecondsOffset
        : unit.endTs - phraseUnit.endTs,
    });
  });

  return { phraseClipIndices, replacementClips };
}
